import { Amount, Button, Link, Text, useToast } from '@razorpay/blade/components';
import api from 'api';
import { getErrorsAsString } from 'components/Kyc/utils';
import ComponentSection from 'components/PayDetails/components/generics/ComponentSection';
import { routePaths } from 'components/Routes/data';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { format } from 'date-fns';
import { useFormWithProvider } from 'forms';
import { useSearchParams } from 'hooks/useSearchParams';
import React, { FormEvent } from 'react';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { AppError } from 'utils/AppError';
import CtcPreview from './components/CtcPreview';
import { SummaryView, SummaryViewItem } from './components/SummaryView';
import { ReviseSalaryFormData, reviseSalarySchema } from './constants';
import { createUpdatePayPayload } from './utils';

const ReviewStep = () => {
  const history = useHistory();
  const toast = useToast();

  const { validate, formData } = useFormWithProvider<ReviseSalaryFormData>();

  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId) as number;
  const userId = useSearchParams().urlParams.get('userId') ?? loggedInUserId.toString();

  const updateSalaryMutation = useMutation({
    mutationFn: api.compensationMutations.updatePay,
    onSuccess: () => {
      toast.show({
        content: 'Salary details updated successfully',
        color: 'positive',
        duration: 3000,
      });
      history.push({
        pathname: routePaths.payDetails.compensation.root,
        search: `?userId=${userId}`,
      });
    },
    onError: (e: AppError) => {
      toast.show({
        content: getErrorsAsString(e),
        color: 'negative',
        duration: 3000,
      });
    },
  });

  if (!formData.revisedCompensation) {
    return (
      <Redirect
        to={{
          pathname: routePaths.payDetails.compensation.edit.salaryDetails,
          search: userId ? `?userId=${userId}` : '',
        }}
      />
    );
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Submit the revised salary (API call or state update can be added here)
    updateSalaryMutation.mutate(
      createUpdatePayPayload({
        userId: userId!,
        annualSalary: parseInt(formData.revisedCompensation ?? '0'),
        effectiveFrom: formData.effectivePayrollMonth!,
      }),
    );
  };

  const isFormValid = validate(reviseSalarySchema, { sideEffects: false });

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper maxWidth="600px">
        <SummaryView>
          <SummaryViewItem label="Revised CTC">
            <Amount
              size="large"
              weight="medium"
              value={parseInt(formData.revisedCompensation ?? '0')}
              suffix="none"
            />
          </SummaryViewItem>
          <SummaryViewItem label="Monthly CTC">
            <Amount
              size="large"
              weight="medium"
              value={parseInt(formData.revisedCompensation ?? '0') / 12}
              suffix="none"
            />
          </SummaryViewItem>
          <SummaryViewItem label="Effective from">
            <Text size="large" weight="medium">
              {formData.effectiveFrom ? format(formData.effectiveFrom, 'dd MMMM, yyyy') : ''}
            </Text>
          </SummaryViewItem>
          <SummaryViewItem label="Current template">
            <Link href={'/companyOnboarding?step=6'} size="large">
              Organization structure
            </Link>
          </SummaryViewItem>
        </SummaryView>
        <ComponentSection marginTop="spacing.8">
          {formData.ctcData ? <CtcPreview ctcData={formData.ctcData} /> : null}
        </ComponentSection>
      </StepContentWrapper>
      <StepActionsWrapper maxWidth="600px">
        <Button
          variant="secondary"
          onClick={() => history.goBack()}
          isDisabled={updateSalaryMutation.isLoading}>
          Back: Salary Details
        </Button>
        <Button type="submit" isDisabled={!isFormValid} isLoading={updateSalaryMutation.isLoading}>
          Confirm
        </Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default ReviewStep;
